import { type PolymorphicProps, useSlotProps } from '@mui/base'
import { type ComponentProps, type ElementType, type JSX } from 'react'

type ProductSearchFlagOwnProps<RootComponentType extends ElementType = 'span'> =
  {
    ref?: ComponentProps<RootComponentType>['ref']

    slotProps?: {
      root?: ComponentProps<RootComponentType>
    }

    slots?: {
      root?: RootComponentType
    }
  }

interface ProductSearchFlagTypeMap<
  RootComponentType extends ElementType = 'span',
> {
  defaultComponent: RootComponentType
  props: ProductSearchFlagOwnProps<RootComponentType>
}

export type ProductSearchFlagProps<
  RootComponentType extends ElementType =
    ProductSearchFlagTypeMap['defaultComponent'],
> = PolymorphicProps<
  ProductSearchFlagTypeMap<RootComponentType>,
  RootComponentType
>

export const ProductSearchFlag = <
  RootComponentType extends ElementType = 'span',
>(
  props: ProductSearchFlagProps<RootComponentType>,
): JSX.Element => {
  const { ref, slotProps, slots, ...restProps } = props

  const Root = slots?.root ?? 'span'
  const rootProps = useSlotProps({
    additionalProps: {
      /**
       * Explicitly pass `ref` to `additionalProps` so `useSlotProps` resolves it,
       * if it's in `restProps`, `rootProps.ref` will be resolved to `null`.
       */
      ref,
    },
    className: 'product-search-flag',
    elementType: Root,
    externalForwardedProps: restProps,
    externalSlotProps: slotProps?.root,
    ownerState: undefined,
  })

  return <Root {...rootProps} />
}
